import React, { useState, useEffect } from 'react';
import './AddFormModal.css';

const USER_TYPES = ['Admin', 'Student', 'Officer']; // no Driver
const NON_STUDENT_ID_VALUE = 'N/A'; // auto-used when not a Student

const AddFormModal = ({ columns, onAdd, onClose }) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [canSubmit, setCanSubmit] = useState(true); // true by default for non-user tables

  // Detect if this modal is being used for the Users table
  const isUserTable = columns.some((col) => col.key === 'usertype');

  const handleChange = (key, value) => {
    let newValue = value;

    if (isUserTable) {
      // Users-table-specific tweaks
      if (key === 'phone') {
        // Digits only
        newValue = value.replace(/\D/g, '');
      }

      if (key === 'username') {
        // Treat as email
        newValue = value.trim();
      }

      if (key === 'usertype') {
        setFormData((prev) => ({
          ...prev,
          usertype: newValue,
          // If changing away from Student, clear studentID (will be set to NON_STUDENT_ID_VALUE on submit)
          studentID: newValue === 'Student' ? prev.studentID : '',
        }));
        return;
      }
    }

    setFormData((prev) => ({ ...prev, [key]: newValue }));
  };

  const validateUserForm = (data) => {
    const newErrors = {};

    // Required fields for Users table
    const requiredKeys = ['username', 'usertype', 'firstname', 'lastname', 'password'];
    requiredKeys.forEach((k) => {
      if (!data[k]) {
        newErrors[k] = 'Required field.';
      }
    });

    // Email format + @lawrence.edu for username
    if (data.username) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(data.username)) {
        newErrors.username = 'Enter a valid email address.';
      } else if (!data.username.toLowerCase().endsWith('@lawrence.edu')) {
        newErrors.username = 'Email must be a @lawrence.edu address.';
      }
    }

    // User type must be one of the allowed values
    if (data.usertype && !USER_TYPES.includes(data.usertype)) {
      newErrors.usertype = 'Invalid user type.';
    }

    // Phone: optional, but if present should be reasonable length
    if (data.phone && data.phone.length < 10) {
      newErrors.phone = 'Phone number looks too short.';
    }

    // Student ID required if Student
    if (data.usertype === 'Student' && !data.studentID) {
      newErrors.studentID = 'Student ID is required for Students.';
    }

    return newErrors;
  };

  // Re-validate whenever formData changes (for Users table)
  useEffect(() => {
    if (isUserTable) {
      const newErrors = validateUserForm(formData);
      setErrors(newErrors);
      setCanSubmit(Object.keys(newErrors).length === 0);
    } else {
      setErrors({});
      setCanSubmit(true);
    }
  }, [formData, isUserTable]);

  const handleSubmit = () => {
    let submissionData = { ...formData };

    if (isUserTable) {
      const newErrors = validateUserForm(submissionData);
      setErrors(newErrors);
      if (Object.keys(newErrors).length > 0) {
        return; // don’t submit if invalid
      }

      // Auto-fill studentID for non-students
      if (submissionData.usertype !== 'Student') {
        submissionData.studentID = NON_STUDENT_ID_VALUE;
      }
    }

    onAdd(submissionData);
    onClose();
  };

  // For platformsCsv checkboxes (Announcements)
  const handlePlatformsChange = (platform, checked) => {
    const current = (formData.platformsCsv || '')
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    let next;
    if (checked) {
      next = Array.from(new Set([...current, platform]));
    } else {
      next = current.filter((p) => p !== platform);
    }

    handleChange('platformsCsv', next.join(','));
  };

  const renderField = (col) => {
    const key = col.key;
    const value = formData[key] ?? '';

    // ---------- USERS TABLE SPECIAL FIELDS ----------
    if (isUserTable) {
      // User Type dropdown
      if (key === 'usertype') {
        return (
          <select
            value={value}
            onChange={(e) => handleChange(key, e.target.value)}
          >
            <option value="">Select a user type</option>
            {USER_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        );
      }

      // Student ID only visible if usertype === 'Student'
      if (key === 'studentID') {
        if (formData.usertype !== 'Student') {
          return null; // hide field entirely when not Student
        }
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleChange(key, e.target.value)}
          />
        );
      }

      // Phone: digits only (logic in handleChange)
      if (key === 'phone') {
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleChange(key, e.target.value)}
            placeholder="Digits only"
          />
        );
      }

      // username acts as Email
      if (key === 'username') {
        return (
          <input
            type="email"
            value={value}
            onChange={(e) => handleChange(key, e.target.value)}
            placeholder="user@lawrence.edu"
          />
        );
      }
    }

    // ---------- ANNOUNCEMENT / SHARED FIELDS ----------

    // MESSAGE → textarea
    if (key === 'message') {
      return (
        <textarea
          value={value}
          onChange={(e) => handleChange(key, e.target.value)}
        />
      );
    }

    // SEVERITY → dropdown
    if (key === 'severity') {
      return (
        <select
          value={value || 'info'}
          onChange={(e) => handleChange(key, e.target.value)}
        >
          <option value="info">info</option>
          <option value="warning">warning</option>
          <option value="critical">critical</option>
        </select>
      );
    }

    // AUDIENCE → dropdown
    if (key === 'audience') {
      return (
        <select
          value={value || 'All'}
          onChange={(e) => handleChange(key, e.target.value)}
        >
          <option value="All">All</option>
          <option value="Student">Student</option>
          <option value="Officer">Officer</option>
          <option value="Driver">Driver</option>
          <option value="Admin">Admin</option>
        </select>
      );
    }

    // BOOLEAN FIELDS → Yes/No dropdown
    if (key === 'required' || key === 'showOncePerVersion' || key === 'enabled') {
      return (
        <select
          value={value === '' ? '' : String(value)}
          onChange={(e) => handleChange(key, e.target.value)}
        >
          <option value="">(select)</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      );
    }

    // DATE/TIME → datetime-local
    if (key === 'startAt' || key === 'endAt') {
      return (
        <input
          type="datetime-local"
          value={value}
          onChange={(e) => handleChange(key, e.target.value)}
        />
      );
    }

    // PRIORITY → number input
    if (key === 'priority') {
      return (
        <input
          type="number"
          value={value}
          onChange={(e) => handleChange(key, e.target.value)}
        />
      );
    }

    // PLATFORMS → checkboxes (android / ios)
    if (key === 'platformsCsv') {
      const current = (formData.platformsCsv || '')
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);

      const isChecked = (p) => current.includes(p);

      return (
        <div className="platform-checkboxes">
          <label className="checkbox-inline">
            <input
              type="checkbox"
              checked={isChecked('android')}
              onChange={(e) =>
                handlePlatformsChange('android', e.target.checked)
              }
            />
            Android
          </label>
          <label className="checkbox-inline">
            <input
              type="checkbox"
              checked={isChecked('ios')}
              onChange={(e) => handlePlatformsChange('ios', e.target.checked)}
            />
            iOS
          </label>
        </div>
      );
    }

    // LINK URL → url input
    if (key === 'linkUrl') {
      return (
        <input
          type="url"
          value={value}
          onChange={(e) => handleChange(key, e.target.value)}
          placeholder="https://example.com"
        />
      );
    }

    // EVERYTHING ELSE → plain text input
    return (
      <input
        type="text"
        value={value}
        onChange={(e) => handleChange(key, e.target.value)}
      />
    );
  };

  const isUserRequiredField = (key) =>
    isUserTable &&
    ['username', 'usertype', 'firstname', 'lastname', 'password'].includes(key);

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>Add New Entry</h2>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          {/* Scrollable body */}
          <div className="modal-body">
            {columns
              .filter((col) => col.editable !== false)
              .map((col) => {
                // Hide Student ID row completely when not Student (Users table only)
                if (
                  isUserTable &&
                  col.key === 'studentID' &&
                  formData.usertype !== 'Student'
                ) {
                  return null;
                }

                return (
                  <div key={col.key} className="form-group">
                    <label>
                      {col.label}
                      {isUserRequiredField(col.key) && (
                        <span className="required">*</span>
                      )}
                    </label>
                    {renderField(col)}
                    {errors[col.key] && (
                      <div className="error-text">{errors[col.key]}</div>
                    )}
                  </div>
                );
              })}

            {/* Password field for Users table only (detected by username presence) */}
            {columns.some((col) => col.key === 'username') && (
              <div className="form-group">
                <label>
                  Password
                  {isUserTable && <span className="required">*</span>}
                </label>
                <input
                  type="password"
                  value={formData.password || ''}
                  onChange={(e) => handleChange('password', e.target.value)}
                />
                {errors.password && (
                  <div className="error-text">{errors.password}</div>
                )}
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button
              type="button"
              onClick={onClose}
              className="cancel-btn"
            >
              Cancel
            </button>
            <button type="submit" disabled={isUserTable && !canSubmit}>
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddFormModal;
